import React from "react";
import { Package, Calendar, Clock, Zap, XCircle, AlertTriangle, Check } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { useSubscription } from "@/hooks/useSubscription";
import { SubscriptionStatusBadge, SubscriptionTypeBadge } from "@/pages/platform/subscriptions/SubscriptionBadges";
import { Card } from "@/components/ui/card";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
}

// HOTFIX P1: Rewritten for tenant admin (light theme). Previously used
// text-white / bg-white/5 which produced unreadable white-on-white contrast.
export default function AdminSubscription() {
  const { subscription, loading, refresh } = useSubscription();

  return (
    <AdminLayout>
      <div data-testid="admin-subscription-page" className="max-w-3xl">
        <h1 className="font-display font-black text-2xl mb-6 flex items-center gap-2 text-[#3E2723]">
          <Package size={20} className="text-[#3E2723]" /> Langganan Anda
        </h1>

        {loading || !subscription ? (
          <div className="text-[#5C5C5C] p-6 rounded-2xl border border-[#E5E5E5] bg-white text-center" data-testid="admin-sub-loading">
            Memuat…
          </div>
        ) : subscription.status === "NONE" ? (
          <Card className="bg-white border-[#E5E5E5] p-6 rounded-2xl shadow-sm">
            <div className="text-[#5C5C5C] text-sm">
              Belum ada subscription untuk tenant ini. Hubungi Platform untuk mengaktifkan.
            </div>
          </Card>
        ) : (
          <>
            {/* Status banners */}
            {subscription.status === "EXPIRED" && (
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-4 flex gap-3">
                <XCircle size={20} className="text-rose-600 shrink-0" />
                <div>
                  <div className="text-rose-700 font-black text-sm">Subscription Expired</div>
                  <div className="text-xs text-rose-800/80 mt-0.5">
                    Silakan renew agar dapat mengakses fitur dashboard sepenuhnya.
                  </div>
                </div>
              </div>
            )}
            {subscription.status === "SUSPENDED" && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex gap-3">
                <AlertTriangle size={20} className="text-amber-600 shrink-0" />
                <div>
                  <div className="text-amber-700 font-black text-sm">Subscription Suspended</div>
                  <div className="text-xs text-amber-800/80 mt-0.5">
                    Alasan: {subscription.subscription?.suspended_reason || "—"}. Hubungi Platform untuk mengaktifkan kembali.
                  </div>
                </div>
              </div>
            )}
            {(subscription.status === "ACTIVE" || subscription.status === "TRIAL") && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 flex gap-3">
                <Check size={20} className="text-emerald-600 shrink-0" />
                <div>
                  <div className="text-emerald-700 font-black text-sm">
                    {subscription.status === "TRIAL" ? "Trial Aktif" : "Subscription Aktif"}
                  </div>
                  {subscription.remaining_days !== null && (
                    <div className="text-xs text-emerald-800/80 mt-0.5">Berakhir dalam {subscription.remaining_days} hari.</div>
                  )}
                  {subscription.is_lifetime && <div className="text-xs text-emerald-800/80 mt-0.5">Lifetime — tidak ada masa berakhir.</div>}
                </div>
              </div>
            )}

            {/* Detail Card — light theme */}
            <Card className="bg-white border-[#E5E5E5] p-6 rounded-2xl space-y-4 shadow-sm">
              <div className="flex items-center gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] text-[#5C5C5C] uppercase tracking-wider font-bold">Current Plan</div>
                  <div className="text-2xl font-display font-black text-[#3E2723]" data-testid="admin-sub-plan">
                    {subscription.plan_code || "—"}
                  </div>
                </div>
                <div className="ml-auto flex flex-col items-end gap-1">
                  <SubscriptionStatusBadge status={subscription.status} />
                  <SubscriptionTypeBadge type={subscription.subscription_type} size="sm" />
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-3 pt-4 border-t border-[#E5E5E5]">
                <div>
                  <div className="text-[10px] text-[#5C5C5C] uppercase tracking-wider font-bold flex items-center gap-1 mb-1">
                    <Calendar size={10} /> Start
                  </div>
                  <div className="text-sm text-[#3E2723] font-semibold">{formatDate(subscription.start_date)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#5C5C5C] uppercase tracking-wider font-bold flex items-center gap-1 mb-1">
                    <Calendar size={10} /> End
                  </div>
                  <div className="text-sm text-[#3E2723] font-semibold">
                    {subscription.is_lifetime ? <span className="text-amber-600">Lifetime</span> : formatDate(subscription.end_date)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-[#5C5C5C] uppercase tracking-wider font-bold flex items-center gap-1 mb-1">
                    <Clock size={10} /> Remaining Days
                  </div>
                  <div className="text-sm text-[#3E2723] font-semibold" data-testid="admin-sub-remaining">
                    {subscription.remaining_days === null ? (
                      <span className="text-amber-600 font-black">∞</span>
                    ) : (
                      <span className={subscription.remaining_days < 7 ? "text-rose-600 font-black" : ""}>
                        {subscription.remaining_days} hari
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-[#E5E5E5] text-xs text-[#5C5C5C] flex items-center gap-2 flex-wrap">
                <Zap size={12} className="text-amber-600 shrink-0" />
                <span className="flex-1 min-w-[200px]">
                  Package Anda (<strong className="text-[#3E2723]">{subscription.plan_code}</strong>) menentukan fitur default yang aktif. Feature override tenant tetap berlaku di atasnya.
                </span>
                <button
                  onClick={refresh}
                  className="text-[#5C5C5C] hover:text-[#3E2723] font-semibold underline underline-offset-2"
                  data-testid="admin-sub-refresh"
                >
                  Refresh
                </button>
              </div>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
